/**
 * Component Registry for DynamicCriteriaGenerator
 *
 * Maps component types to their React components and provides
 * the createComponentElement function for rendering.
 *
 * Uses TYPE_REGISTRY from typeRegistry.ts for all metadata.
 */

import { ComponentType, ReactNode } from 'react';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import { TYPE_REGISTRY, getEffectiveType } from '../typeRegistry';
import { transformProvider } from '../components/Providers/config';
import {
  CodesComponent,
  EducationalInstitutionsComponent,
  EducationFocusComponent,
  EducationProgramTypeComponent,
  ProviderComponent,
  PayerTypeComponent,
  PayerNameComponent,
  EncountersComponent,
  CodeSetsComponent,
  CRMStatusComponent,
  EntityAttributeComponent,
  EntityTypeComponent,
  FilterSetsComponent,
  OpenPaymentsComponent,
  OpenPaymentsCategoryComponent,
  ThisProviderComponent,
  ThisSiteComponent,
  ProviderListsComponent,
} from '../components';

/**
 * Maps component types to their React component implementations.
 * null means the component is not yet implemented.
 */
const COMPONENT_MAP: Record<CriteriaComponentType, ComponentType<any> | null> =
  {
    codes: CodesComponent,
    code_set: CodeSetsComponent,
    crm_status: CRMStatusComponent,
    education_focus: EducationFocusComponent,
    education_program_type: EducationProgramTypeComponent,
    educational_institution: EducationalInstitutionsComponent,
    entity_attribute: EntityAttributeComponent,
    entity_type: EntityTypeComponent,
    filter_set: FilterSetsComponent,
    open_payments: OpenPaymentsComponent,
    open_payments_category: OpenPaymentsCategoryComponent,
    payer_name: PayerNameComponent,
    payer_type: PayerTypeComponent,
    provider_cohort: EncountersComponent,
    provider_list: ProviderListsComponent,
    providers: ProviderComponent,
    specialties: null, // Not yet implemented
    this_provider: ThisProviderComponent,
    this_site: ThisSiteComponent,
  };

/**
 * Get the React component for a type
 */
export function getComponent(
  type: CriteriaComponentType
): ComponentType<any> | null {
  return COMPONENT_MAP[type];
}

/**
 * Check if a component type has been implemented
 */
export function isImplemented(type: CriteriaComponentType): boolean {
  return COMPONENT_MAP[type] !== null;
}

// === Legacy Exports for Backward Compatibility ===
// These will be removed once all consumers are updated

/**
 * @deprecated Use TYPE_REGISTRY from typeRegistry.ts instead
 */
export interface ComponentConfig {
  Component: ComponentType<any>;
  reduxDataKey: string;
  initialDataPropKey: string;
  getItemIds: (items: any[]) => any;
  defaultConnector: 'ANY' | 'ALL';
  isSingleton?: boolean;
  isStatic?: boolean;
}

/**
 * @deprecated Use TYPE_REGISTRY from typeRegistry.ts instead
 */
export const COMPONENT_REGISTRY: Partial<
  Record<CriteriaComponentType, ComponentConfig>
> = Object.fromEntries(
  Object.entries(TYPE_REGISTRY)
    .filter(([type]) => COMPONENT_MAP[type as CriteriaComponentType] !== null)
    .map(([type, meta]) => [
      type,
      {
        Component: COMPONENT_MAP[type as CriteriaComponentType] as ComponentConfig['Component'],
        reduxDataKey: meta.reduxDataField,
        initialDataPropKey: meta.initialDataPropKey,
        getItemIds: meta.getItemIds,
        defaultConnector: meta.defaultConnector,
        isSingleton: meta.typeClass === 'singleton',
        isStatic: meta.typeClass === 'static',
      },
    ])
) as Partial<Record<CriteriaComponentType, ComponentConfig>>;

/**
 * @deprecated Use TYPE_REGISTRY from typeRegistry.ts instead
 */
export const TYPE_CONFIG: Record<
  string,
  { dataKey: string; getItemId: (item: any) => string }
> = Object.fromEntries(
  Object.entries(TYPE_REGISTRY).map(([type, meta]) => [
    type,
    {
      dataKey: meta.reduxDataField,
      getItemId: meta.getItemId,
    },
  ])
);

/**
 * @deprecated Use getTypeMetadata from typeRegistry.ts instead
 */
export const getTypeConfig = (
  type: CriteriaComponentType
): { dataKey: string; getItemId: (item: any) => string } => {
  const meta = TYPE_REGISTRY[type];
  if (meta) {
    return { dataKey: meta.reduxDataField, getItemId: meta.getItemId };
  }
  return { dataKey: type, getItemId: (item) => item.id };
};

// === Main Rendering Function ===

interface RenderComponentParams {
  componentId: string;
  type: CriteriaComponentType;
  inputData: any;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  updateComponentInput: (componentId: string, data: any) => void;
}

/**
 * Renders a component based on its type using the registry.
 * Returns the React element to be rendered in a portal.
 *
 * Handles ui_key to restore this_site from this_provider when editing.
 * The API stores this_site as this_provider with ui_key='this_site'.
 */
export function createComponentElement({
  componentId,
  type,
  inputData,
  onUpdate,
  onRemove,
  updateComponentInput,
}: RenderComponentParams): ReactNode {
  // Handle ui_key for this_site restoration
  const effectiveType = getEffectiveType(type, inputData?.ui_key);
  const metadata = TYPE_REGISTRY[effectiveType];
  const Component = COMPONENT_MAP[effectiveType];

  if (!Component) {
    console.warn(`Unknown or unimplemented component type: ${type}`);
    return null;
  }

  const {
    reduxDataField,
    initialDataPropKey,
    getItemIds,
    defaultConnector,
    typeClass,
  } = metadata;

  const isStatic = typeClass === 'static';
  const isSingleton = typeClass === 'singleton';

  const initialConnector =
    (inputData.connector as 'ANY' | 'ALL') || defaultConnector;

  const baseProps = {
    componentId,
    initialConnector,
    onUpdate,
    onRemove,
  };

  // Handle static components (this_provider, this_site)
  if (isStatic) {
    updateComponentInput(componentId, {
      type: effectiveType,
      connector: 'ANY',
    });
    return <Component {...baseProps} />;
  }

  // Determine initial data for the component
  // Filter out null values that can occur when API data has unresolved items
  let initialData: any[];
  if (inputData.ui_metadata && inputData.ui_metadata.length > 0) {
    initialData = inputData.ui_metadata.filter(
      (item: unknown) => item !== null && item !== undefined
    );
    // Backend returns {provider_id, name} but ProviderItem requires {id, displayLabel, ...}.
    // Normalize any items that are missing id (backend-generated) through transformProvider.
    if (effectiveType === 'providers') {
      initialData = initialData.map((item: any) =>
        item.id ? item : transformProvider(item)
      );
    }
  } else if (isSingleton && inputData[reduxDataField]) {
    // For singleton types, wrap the single value in an array
    initialData = [inputData[reduxDataField]];
  } else {
    initialData = inputData[reduxDataField] || [];
  }

  // Build component props
  const componentProps = {
    ...baseProps,
    [initialDataPropKey]: initialData,
    // Pass flag indicating if this was created from a placeholder
    fromPlaceholder: inputData._fromPlaceholder || false,
  };

  // If we have pre-selected data from slash command, register with Redux
  const hasPreselectedData =
    inputData.ui_metadata && inputData.ui_metadata.length > 0;
  if (hasPreselectedData) {
    updateComponentInput(componentId, {
      type: effectiveType,
      [reduxDataField]: getItemIds(initialData),
      ui_metadata: initialData,
      connector: initialConnector,
    } as any);
  }

  return <Component {...componentProps} />;
}
